import { AssetGroup } from '@play-co/timestep-core';
import { ButtonView, ImageScaleView, View } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import DreamDriftSleepZ from 'src/game/dreamDrift/components/DreamDriftSleepZ';
import {
  dreamDriftCostumeAssets,
  dreamDriftWearPet,
} from 'src/game/dreamDrift/dreamDrift.costumes';
import { createUIPet } from 'src/game/pet/pet.utils';
import { trackFirstButtonTap } from 'src/lib/analytics/firstButtonTap';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import { getScreenDimensions } from 'src/lib/utils';
import {
  dreamDriftHeroes,
  type DreamDriftHero,
} from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import type { PetSpeciesID } from 'src/replicant/pet';
import { getPetName } from 'src/replicant/pet/pet.getters';
import StateObserver, { user } from 'src/StateObserver';

/** Native width of the supplied Dream Drift completion design. */
const DESIGN_WIDTH = 720;

/** Native height of the supplied Dream Drift completion design. */
const DESIGN_HEIGHT = 1280;

/** Fullscreen dream recap shown after the current Dream Drift is finished. */
export default class PopupDreamDriftComplete extends PopupBasicLazy {
  /** Native-resolution composition containing the recap and sleeping pet. */
  private readonly content: View;

  /** Floating dream bubble populated with the dressed pet during initialization. */
  private readonly bubble: View;

  /**
   * Create the fullscreen completion composition and tap-to-continue acknowledgement.
   *
   * @param opts Popup parent and close callback supplied by the popup registry.
   */
  constructor(opts: { superview: View; close: () => void }) {
    // Size the borderless popup to the complete current viewport.
    const width = opts.superview.style.width;
    const { height } = getScreenDimensions();

    // Keep the completion recap above the Dream Drift map without a panel frame.
    super({
      superview: opts.superview,
      width,
      height,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      close: opts.close,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_complete',
      },
      zIndex: 20000,
    });

    // Match the fullscreen information popup's dark overlay treatment.
    this.bg.updateOpts({ backgroundColor: 'rgba(71, 24, 67, 0.9)' });

    // The player acknowledges the recap by tapping anywhere on the screen.
    this.buttonClose.removeFromSuperview();

    // Fit the native-resolution composition inside shorter or narrower screens.
    const contentScale = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT);
    this.content = new View({
      superview: this.box,
      width: DESIGN_WIDTH,
      height: DESIGN_HEIGHT,
      x: width / 2,
      y: height / 2,
      scale: contentScale,
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    // Display the supplied localized completion heading at its native game scale.
    const locale = StateObserver.getLocale();
    new ImageScaleView({
      superview: this.content,
      image: `assets/ui/dreamDrift/popupComplete/title_${locale}.png`,
      width: downscale(locale === 'en' ? 912 : 1080),
      height: downscale(locale === 'en' ? 103 : 124),
      x: DESIGN_WIDTH / 2,
      y: 177,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // End the pet's adventure with plain Title-font copy and explicit line breaks.
    new LangBitmapFontTextView({
      superview: this.content,
      x: DESIGN_WIDTH / 2,
      y: 300,
      width: 630,
      height: 110,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Title',
      size: 32,
      color: '#FFFFFF',
      canHandleEvents: false,
      localeText: () =>
        i18n('dreamDrift.complete.title', { pet_name: getPetName(user()) }),
    });

    // Group the dream bubble and dressed pet so they float together.
    const bubbleY = 600;
    this.bubble = new View({
      superview: this.content,
      width: downscale(837),
      height: downscale(602),
      x: DESIGN_WIDTH / 2,
      y: bubbleY,
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    // Preserve the supplied cloud's proportions behind the hero pet.
    new ImageScaleView({
      superview: this.bubble,
      image: 'assets/ui/dreamDrift/popupComplete/bubble.png',
      width: this.bubble.style.width,
      height: this.bubble.style.height,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Connect the dream bubble to the sleeping pet with the supplied thought dots.
    const bubbleDotsY = 848;
    const bubbleDots = new ImageScaleView({
      superview: this.content,
      image: 'assets/ui/dreamDrift/popupComplete/bubble_dots.png',
      width: downscale(203),
      height: downscale(123),
      x: 358,
      y: bubbleDotsY,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Float the bubble and dots together, with subtler motion for the dots.
    this.bubble.setTick(() => {
      // Move the bubble and hero through a sixteen-pixel range over a slow cycle.
      const floatOffset = Math.sin(performance.now() / 1200) * 8;
      this.bubble.updateOpts({ y: bubbleY + floatOffset });

      // Keep the dots in sync with half of the bubble's movement.
      bubbleDots.updateOpts({ y: bubbleDotsY + floatOffset / 2 });
    });

    // Place the alarm clock beside the undressed sleeping pet.
    new ImageScaleView({
      superview: this.content,
      image: 'assets/ui/dreamDrift/popupComplete/alarm_clock.png',
      width: downscale(176),
      height: downscale(179),
      x: 220,
      y: 880,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Tease the next weekly dream below the completed pet presentation.
    new LangBitmapFontTextView({
      superview: this.content,
      x: DESIGN_WIDTH / 2,
      y: 1030,
      width: 630,
      height: 110,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Title',
      size: 32,
      color: '#FFFFFF',
      canHandleEvents: false,
      localeText: () =>
        i18n('dreamDrift.complete.message', { pet_name: getPetName(user()) }),
    });

    // Reuse the shared localized tap-to-continue artwork.
    new ImageScaleView({
      superview: this.content,
      image: `assets/ui/dreamDrift/common/tap_to_continue_${locale}.png`,
      width: downscale(locale === 'en' ? 569 : 433),
      height: downscale(locale === 'en' ? 87 : 84),
      x: DESIGN_WIDTH / 2,
      y: 1170,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Close on a full-screen tap while preserving the existing acknowledgement tracking.
    new ButtonView({
      superview: this.box,
      width,
      height,
      zIndex: 1,
      onClick: () => {
        // Keep completion acknowledgements under the existing analytics identifier.
        trackFirstButtonTap({
          feature: 'dream_drift',
          $subFeature: 'dream_drift_complete_ok',
        });

        // Confirm the acknowledgement with the standard positive popup sound.
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  /**
   * Create both pet presentations and display the completion recap.
   *
   * @param args Optional species and hero overrides; omitted values use the player's pet and completed run.
   * @returns Nothing.
   */
  public init(args: { species?: PetSpeciesID; hero?: DreamDriftHero }): void {
    // Create the happy pet only after initialization provides any species override.
    const pet = createUIPet('happy', args.species);

    // Place the happy hero below the bubble's center without changing the real pet's outfit.
    pet.updateOpts({
      superview: this.bubble,
      x: this.bubble.style.width / 2,
      y: this.bubble.style.height / 2 + 30,
      scale: 0.96,
      canHandleEvents: false,
    });

    // Prefer the explicit hero while retaining the completed run's costume by default.
    const storedHero = args.hero ?? user().dreamDrift?.instance?.hero;
    const hero = dreamDriftHeroes.find(
      (configuredHero) => configuredHero === storedHero,
    );

    // Retain a plain happy pet when the stored hero is missing or unsupported.
    if (hero) dreamDriftWearPet(pet, hero);

    // Use the same species for the sleeping pet without applying any costume.
    const sleepingPet = createUIPet('sleeping', args.species);
    sleepingPet.updateOpts({
      superview: this.content,
      x: 515,
      y: 878,
      scale: 0.6,
      canHandleEvents: false,
    });

    // Place the shared animated Z decoration above the sleeping pet.
    new DreamDriftSleepZ({
      superview: this.content,
      x: 555,
      y: 807,
    });

    // Start the shared popup lifecycle without additional open arguments.
    super.init({});
  }

  /**
   * Load the supplied completion art, shared tap prompt, and hero costumes.
   *
   * @returns Asset group containing every image used by the completion composition.
   */
  protected getAssetsGroup(): AssetGroup {
    // Load both locales and every supported final hero before showing the recap.
    return new AssetGroup([
      ...dreamDriftCostumeAssets,
      ...AssetGroup.constructURLs([
        'assets/ui/dreamDrift/common',
        'assets/ui/dreamDrift/popupComplete',
      ]),
    ]);
  }
}
